import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { hitApi, commonToast, assetsUrlPath } from "../../../components/commonAction";
import svg from "../../../components/svg";
import BillingTabs from "./BillingTabs";
import "../../../styles/admin-drawer.css";

const EMPTY_FORM = {
    // core
    code: "", name: "", description: "", logoUrl: "", isActive: true,
    // launch metadata (Phase 7)
    url: "", launchUrl: "", icon: "", toolTip: "", status: 0,
    // marketing list ids (Phase 7)
    getresponseListId: "", aweberListId: "",
    // provisioning (Phase 7)
    provisionUrl: "", provisionSecret: "",
    // marketing showcase
    marketingUrl: "",
};

export default function Products({ pageTitle }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [testingId, setTestingId] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Deferred image upload: the picked file is held locally and previewed
    // via a blob URL; the actual S3/disk upload only fires on Save.
    const [pendingImageFile, setPendingImageFile] = useState(null);
    const [pendingImagePreview, setPendingImagePreview] = useState('');

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        // Revoke any previously-staged blob to avoid a memory leak.
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImageFile(file);
        setPendingImagePreview(URL.createObjectURL(file));
    };

    const clearPendingImage = () => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImageFile(null);
        setPendingImagePreview('');
    };

    const removeProductImage = () => {
        clearPendingImage();
        setForm(f => ({ ...f, logoUrl: '' }));
    };

    // Uploads the staged file (if any) and resolves to the path to save.
    // Returns the existing form.logoUrl unchanged when nothing is staged.
    const uploadPendingImage = () => new Promise((resolve, reject) => {
        if (!pendingImageFile) { resolve(form.logoUrl); return; }
        const fd = new FormData();
        fd.append('product_image', pendingImageFile);
        hitApi(
            {
                url: 'admin/billing/products/upload-image',
                method: 'POST',
                data: fd,
                isFormData: true,
                alert: false,
            },
            (resp) => {
                if (resp?.status) {
                    resolve(resp.data?.path || '');
                } else {
                    reject(new Error(resp?.message || 'Image upload failed.'));
                }
            }
        );
    });

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setDrawerOpen(true);
    };
    const closeDrawer = () => {
        setDrawerOpen(false);
    };
    // Reset form a tick after the drawer slides out so the close animation is clean.
    useEffect(() => {
        if (drawerOpen) return;
        // Drop any staged image immediately so we don't leak the blob URL.
        clearPendingImage();
        const t = setTimeout(() => {
            setEditingId(null);
            setForm(EMPTY_FORM);
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerOpen]);

    // Close on Escape
    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerOpen]);

    const loadProducts = () => {
        setLoading(true);
        hitApi({ url: "admin/billing/products", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setProducts(resp.data?.products || []);
        });
    };

    useEffect(loadProducts, []);

    const startEdit = (p) => {
        setEditingId(p._id);
        setForm({
            code: p.code,
            name: p.name,
            description: p.description || "",
            logoUrl: p.logoUrl || "",
            isActive: !!p.isActive,
            url: p.url || "",
            launchUrl: p.launchUrl || "",
            icon: p.icon || "",
            toolTip: p.toolTip || "",
            status: typeof p.status === "number" ? p.status : 0,
            getresponseListId: p.getresponseListId || "",
            aweberListId: p.aweberListId || "",
            provisionUrl: p.provisionUrl || "",
            provisionSecret: "",
            marketingUrl: p.marketingUrl || "",
        });
        setDrawerOpen(true);
    };
    const cancelEdit = () => { closeDrawer(); };

    const testProvision = (p, eventType = "entitlement.granted") => {
        setTestingId(p._id);
        setTestResult(null);
        hitApi({
            url: `admin/billing/products/${p._id}/test-provision`,
            method: "POST",
            data: { eventType },
            alert: false,
        }, (resp) => {
            setTestingId(null);
            const d = resp?.data || {};
            setTestResult({
                productCode: p.code,
                eventType,
                ok: !!d.ok,
                httpStatus: d.httpStatus || 0,
                url: d.url || p.provisionUrl,
                error: d.error || null,
                payload: d.payload || null,
            });
            if (resp?.status && d.ok) {
                commonToast("success", resp.message);
            } else {
                commonToast("error", resp?.message || "Test provision failed.");
            }
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.code.trim() || !form.name.trim()) {
            commonToast("error", "Code and name are required.");
            return;
        }
        setSubmitting(true);

        // 1) Upload the staged image first (if the admin picked one this session).
        //    Nothing hits S3 until this step — picking a file only set local state.
        let resolvedLogoUrl;
        try {
            resolvedLogoUrl = await uploadPendingImage();
        } catch (err) {
            setSubmitting(false);
            commonToast("error", err.message || "Image upload failed.");
            return;
        }

        // 2) Build the product body using the path returned by the upload.
        const url = editingId
            ? `admin/billing/products/${editingId}`
            : `admin/billing/products`;
        const method = editingId ? "PATCH" : "POST";

        let body;
        if (editingId) {
            body = {
                name: form.name,
                description: form.description,
                logoUrl: resolvedLogoUrl,
                isActive: form.isActive,
                url: form.url,
                launchUrl: form.launchUrl,
                icon: form.icon,
                toolTip: form.toolTip,
                status: Number(form.status) || 0,
                getresponseListId: form.getresponseListId,
                aweberListId: form.aweberListId,
                provisionUrl: form.provisionUrl,
                marketingUrl: form.marketingUrl,
            };
            if (form.provisionSecret && form.provisionSecret.length > 0) {
                body.provisionSecret = form.provisionSecret;
            }
        } else {
            body = { ...form, logoUrl: resolvedLogoUrl, status: Number(form.status) || 0 };
        }

        hitApi({ url, method, data: body }, (resp) => {
            setSubmitting(false);
            if (resp?.status) {
                commonToast("success", resp.message);
                clearPendingImage();
                closeDrawer();
                loadProducts();
            }
        });
    };

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap"><div className="pxl-headingTop"><h2 className="pxl-rb-header-heading">Billing</h2></div></div>
            <BillingTabs />

            <div className="row g-3">
                <div className="col-12">
                    <div className="pxl-panel">
                        <div className="pxl-panel-header">
                            <span>Products ({products.length})</span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-light" onClick={loadProducts}>Refresh</button>
                                <button className="pxl-drawer-new-btn" onClick={openCreate}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                    New Product
                                </button>
                            </div>
                        </div>
                        <div className="pxl-panel-body pxl-panel-body--flush">
                            {!loading && products.length === 0 ? (
                                <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No products yet. Create one on the left.</div>
                            ) : (
                                <div className="pxl_table table-responsive">
                                    <table className="pxl_table">
                                        <thead>
                                            <tr>
                                                <th>Code</th>
                                                <th>Name</th>
                                                <th>Dashboard</th>
                                                <th>Status</th>
                                                <th>Provision</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[0, 1, 2, 3, 4].map(i => (
                                                        <tr key={i}>
                                                            <td><span className="pxl-sk" style={{ width: '40%', height: 13, display: 'block' }} /></td>
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <span className="pxl-sk" style={{ width: 36, height: 36, borderRadius: 8, display: 'inline-block' }} />
                                                                    <span className="pxl-sk" style={{ width: '55%', height: 13, display: 'inline-block' }} />
                                                                </div>
                                                            </td>
                                                            <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: '45%', height: 13, display: 'block' }} /></td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: 8 }}>
                                                                    <span className="pxl-sk" style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-block' }} />
                                                                    <span className="pxl-sk" style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-block' }} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            ) : products.map(p => (
                                                <tr key={p._id}>
                                                    <td><p style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}>{p.code}</p></td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div
                                                                className={`pxl-prod-image-thumb${(p.logoUrl || (svg.app && svg.app[p.icon])) ? '' : ' pxl-prod-image-thumb--empty'}`}
                                                                style={{ width: 36, height: 36, flexShrink: 0 }}
                                                            >
                                                                {p.logoUrl
                                                                    ? <img src={assetsUrlPath(p.logoUrl)} alt={p.name} />
                                                                    : (svg.app && svg.app[p.icon])
                                                                        ? svg.app[p.icon]
                                                                        : <span>{(p.name || '?').charAt(0).toUpperCase()}</span>}
                                                            </div>
                                                            <p style={{ margin: 0 }}>{p.name}</p>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {!p.isActive
                                                            ? <span className="pxl-pill pxl-pill--muted" title="Not returned to the customer dashboard">hidden</span>
                                                            : p.status === 1
                                                                ? <span className="pxl-pill pxl-pill--success" title="Shown in the Apps section on the customer dashboard">live</span>
                                                                : <span className="pxl-pill pxl-pill--info" title="Shown in the Upcoming section on the customer dashboard">upcoming</span>}
                                                    </td>
                                                    <td>
                                                        {p.isActive
                                                            ? <span className="pxl-pill pxl-pill--success">active</span>
                                                            : <span className="pxl-pill pxl-pill--muted">inactive</span>}
                                                    </td>
                                                    <td>
                                                        {p.provisionUrl
                                                            ? <span className="pxl-pill pxl-pill--info" title={p.provisionUrl}>configured</span>
                                                            : <span style={{ color: "#8a8fa6", fontSize: 12 }}>—</span>}
                                                    </td>
                                                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                                        <button className="btn btn-sm btn-link" onClick={() => startEdit(p)}>Edit</button>
                                                        {p.provisionUrl && (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm btn-link"
                                                                    disabled={testingId === p._id}
                                                                    onClick={() => testProvision(p, "entitlement.granted")}
                                                                    title="Send a synthetic entitlement.granted event to this product's provision URL"
                                                                >
                                                                    {testingId === p._id ? "Sending…" : "Test grant"}
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-link"
                                                                    disabled={testingId === p._id}
                                                                    onClick={() => testProvision(p, "entitlement.revoked")}
                                                                    title="Send a synthetic entitlement.revoked event to this product's provision URL"
                                                                >
                                                                    Test revoke
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {testResult && (
                        <div className="pxl-panel mt-3">
                            <div className="pxl-panel-header">
                                <span>
                                    Last test — <code>{testResult.productCode}</code> · {testResult.eventType}{" "}
                                    {testResult.ok
                                        ? <span className="pxl-pill pxl-pill--success">HTTP {testResult.httpStatus}</span>
                                        : <span className="pxl-pill pxl-pill--danger">{testResult.httpStatus ? `HTTP ${testResult.httpStatus}` : "failed"}</span>}
                                </span>
                                <button className="btn btn-sm btn-light" onClick={() => setTestResult(null)}>Close</button>
                            </div>
                            <div className="pxl-panel-body">
                                <div style={{ color: "#8a8fa6", fontSize: 12, marginBottom: 8 }}>
                                    POST <code>{testResult.url}</code>
                                </div>
                                {testResult.error && (
                                    <div className="pxl-status-banner pxl-status-banner--danger" style={{ marginBottom: 8 }}>{testResult.error}</div>
                                )}
                                <details>
                                    <summary style={{ color: "#8a8fa6", fontSize: 12, cursor: "pointer" }}>View payload</summary>
                                    <pre style={{ background: "#f7f8fb", padding: 12, marginTop: 8, fontSize: 12, maxHeight: 320, overflow: "auto", borderRadius: 6 }}>
{JSON.stringify(testResult.payload, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Right-side drawer: create / edit product ── */}
            <div
                className={`pxl-drawer-backdrop${drawerOpen ? ' is-open' : ''}`}
                onClick={closeDrawer}
            />
            <aside
                className={`pxl-drawer${drawerOpen ? ' is-open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={editingId ? 'Edit product' : 'New product'}
            >
                <div className="pxl-drawer-header">
                    <div>
                        <h3 className="pxl-drawer-title">{editingId ? 'Edit Product' : 'New Product'}</h3>
                        <p className="pxl-drawer-sub">
                            {editingId
                                ? `Code: ${form.code}`
                                : 'Add a new product to the billing catalog.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pxl-drawer-close"
                        onClick={closeDrawer}
                        title="Close"
                        aria-label="Close"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={submit} className="pxl-drawer-body" id="productForm">
                    {/* === Identity === */}
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Code <span className="pxl-star-red">*</span></label>
                        <input
                            value={form.code}
                            onChange={e => setForm({ ...form, code: e.target.value })}
                            disabled={!!editingId}
                            placeholder="e.g. showcaseai"
                        />
                        <small>Lowercase, no spaces. Cannot be changed after creation.</small>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Name <span className="pxl-star-red">*</span></label>
                        <input
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Description</label>
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Product URL <span style={{ color: '#8a8fa6', fontWeight: 400 }}>(for "Explore" button)</span></label>
                        <input
                            value={form.marketingUrl}
                            onChange={e => setForm({ ...form, marketingUrl: e.target.value })}
                            placeholder="https://pixalab.ai/showcaseai"
                        />
                        <small>Public marketing page. Used in affiliate emails and other promo surfaces.</small>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Product image</label>
                        <div className="pxl-prod-image-row">
                            {pendingImagePreview ? (
                                <div className="pxl-prod-image-thumb">
                                    <img src={pendingImagePreview} alt="" />
                                </div>
                            ) : form.logoUrl ? (
                                <div className="pxl-prod-image-thumb">
                                    <img src={assetsUrlPath(form.logoUrl)} alt="" />
                                </div>
                            ) : (
                                <div className="pxl-prod-image-thumb pxl-prod-image-thumb--empty">SVG</div>
                            )}
                            <label className="btn btn-sm btn-light pxl-prod-image-btn" style={{ cursor: 'pointer' }}>
                                {pendingImagePreview || form.logoUrl ? 'Replace' : 'Upload'}
                                <input
                                    type="file"
                                    accept="image/svg+xml,image/png,image/jpeg,image/webp"
                                    onChange={onPickImage}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            {(pendingImagePreview || form.logoUrl) && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link"
                                    onClick={removeProductImage}
                                >Remove</button>
                            )}
                            {pendingImageFile && (
                                <span style={{ fontSize: 12, color: '#8a8fa6' }}>
                                    Selected — uploads on Save
                                </span>
                            )}
                        </div>
                        <small>SVG preferred; PNG/JPG/WEBP allowed. Stored on S3 in production.</small>
                    </div>
                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="prodActive"
                            checked={form.isActive}
                            onChange={e => setForm({ ...form, isActive: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="prodActive">Active (sellable in tiers)</label>
                    </div>

                    {/* === Launch metadata === */}
                    <div className="pxl-form-section-divider" />
                    <h6 className="pxl-form-section-heading">Launch metadata</h6>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Launch URL</label>
                        <input
                            value={form.url}
                            onChange={e => setForm({ ...form, url: e.target.value })}
                            placeholder="https://app.example.com/sso?token="
                        />
                        <small>Token is appended at runtime by the dashboard.</small>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Override URL <span style={{ color: "#8a8fa6", fontWeight: 400 }}>(optional)</span></label>
                        <input
                            value={form.launchUrl}
                            onChange={e => setForm({ ...form, launchUrl: e.target.value })}
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Icon key</label>
                        <input
                            value={form.icon}
                            onChange={e => setForm({ ...form, icon: e.target.value })}
                            placeholder="showcaseAi / builderApp / pixaSupport / …"
                        />
                        <small>Must match an entry in <code>src/components/svg.js</code>.</small>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Tooltip</label>
                        <textarea
                            rows={2}
                            value={form.toolTip}
                            onChange={e => setForm({ ...form, toolTip: e.target.value })}
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Dashboard status</label>
                        <select
                            value={form.status}
                            onChange={e => setForm({ ...form, status: Number(e.target.value) })}
                        >
                            <option value={0}>Upcoming (0)</option>
                            <option value={1}>Live (1)</option>
                        </select>
                    </div>

                    {/* === Marketing lists === */}
                    <div className="pxl-form-section-divider" />
                    <h6 className="pxl-form-section-heading">Marketing lists</h6>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>GetResponse list id</label>
                        <input
                            value={form.getresponseListId}
                            onChange={e => setForm({ ...form, getresponseListId: e.target.value })}
                            placeholder="XNsRM"
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>AWeber list id</label>
                        <input
                            value={form.aweberListId}
                            onChange={e => setForm({ ...form, aweberListId: e.target.value })}
                            placeholder="6925122"
                        />
                    </div>

                    {/* === Provisioning === */}
                    <div className="pxl-form-section-divider" />
                    <h6 className="pxl-form-section-heading">Provisioning callback</h6>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Provision URL</label>
                        <input
                            value={form.provisionUrl}
                            onChange={e => setForm({ ...form, provisionUrl: e.target.value })}
                            placeholder="https://app.example.com/api/pixalab-events"
                        />
                        <small>Empty disables the outgoing callback for this product.</small>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Provision bearer token</label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            value={form.provisionSecret}
                            onChange={e => setForm({ ...form, provisionSecret: e.target.value })}
                            placeholder={editingId ? "(leave blank to keep current)" : "Optional"}
                        />
                        <small>Sent as <code>Authorization: Bearer …</code> on every callback.</small>
                    </div>
                </form>

                <div className="pxl-drawer-footer">
                    <button type="submit" form="productForm" className="pxlCommon-btn" disabled={submitting}>
                        {submitting ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save' : 'Create')}
                    </button>
                    <button type="button" className="btn btn-light" onClick={cancelEdit}>Cancel</button>
                </div>
            </aside>
        </div>
    );
}
